import { makeAutoObservable } from 'mobx';
import type RootStore from '../../../stores/RootStore';

/**
 * ProductionScheduleViewStore - Manages UI state for production scheduling views
 *
 * Per CLAUDE.md standards:
 * - NEVER stores API entities directly
 * - Only manages UI state (selections, filters, view modes)
 * - All entity data accessed via computed values from ServiceStores
 * - Route ViewStores are plain classes with makeAutoObservable (NOT extending ViewStore)
 */
class ProductionScheduleViewStore {
    // UI State Only
    selectedScheduleId: string | null = null;
    selectedDepartmentId: string | null = null;
    selectedFormId: string | null = null;
    selectedDate: Date = new Date();

    // View Modes
    viewMode: 'list' | 'calendar' | 'gantt' = 'list';
    calendarView: 'week' | 'month' = 'week';
    showCapacityIndicators = true;
    showConflicts = true;

    // Filters (UI state only)
    filters = {
        department: null as string | null,
        subDepartment: null as string | null,
        form: null as string | null,
        priorityRange: {
            min: null as number | null,
            max: null as number | null
        },
        dateRange: {
            start: new Date(),
            end: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000) // 1 week ahead
        },
        hasAnticipationDate: null as boolean | null,
        status: 'all' as 'all' | 'scheduled' | 'released' | 'completed' | 'issues'
    };

    // Bulk Selection (UI state)
    selectedItems = new Set<string>();
    selectMode = false;

    // Dialog States (UI only)
    dialogs = {
        scheduleModal: false,
        bulkReschedule: false,
        formAssignment: false,
        capacityDetails: false,
        requestSchedule: false,
        conflictResolver: false
    };

    // Form Assignment State
    formAssignment = {
        itemId: null as string | null,
        suggestedForms: [] as any[],
        selectedFormId: null as string | null,
        rememberMapping: false
    };

    // Capacity Request State
    capacityRequest = {
        itemId: null as string | null,
        requestedDate: null as Date | null,
        availableDate: null as Date | null,
        justification: '',
        priority: 5
    };

    // Drag and Drop State
    draggedItemId: string | null = null;
    dragOverDate: Date | null = null;
    dragOverFormId: string | null = null;

    constructor(private rootStore: RootStore) {
        makeAutoObservable(this, {
            rootStore: false
        });
    }

    // ========================================
    // Computed values - Reference ServiceStore entities
    // Per CLAUDE.md: Never store entities, only compute from ServiceStores
    // ========================================

    get schedules() {
        // Access schedules from ServiceStore
        const schedulesStore = this.rootStore.productionSchedulesStore as any;
        return schedulesStore?.entities || [];
    }

    get selectedSchedule() {
        if (!this.selectedScheduleId) return null;
        return this.schedules.find((s: any) => s.id === this.selectedScheduleId);
    }

    get departments() {
        const deptStore = this.rootStore.departmentsStore as any;
        return deptStore?.entities || [];
    }

    get forms() {
        const formsStore = this.rootStore.productionFormsStore as any;
        return formsStore?.entities || [];
    }

    get formsByDepartment() {
        const grouped = new Map<string, any[]>();

        this.forms.forEach((form: any) => {
            const deptId = form.departmentId || 'uncategorized';
            if (!grouped.has(deptId)) {
                grouped.set(deptId, []);
            }
            grouped.get(deptId)!.push(form);
        });

        return grouped;
    }

    get salesOrderLines() {
        // Get all unscheduled sales order lines
        const lineItemsStore = this.rootStore.salesOrderLineItemsStore as any;
        const allLines = lineItemsStore?.entities || [];

        // Filter for released but not scheduled items
        return allLines.filter((line: any) =>
            line.status === 'released' && !line.scheduledDate
        );
    }

    // Row color coding logic
    get itemsWithColorCoding() {
        return this.filteredSchedules.map((item: any) => {
            let colorCode = 'white';

            if (!item.anticipationDate) {
                colorCode = 'red'; // No anticipation date
            } else {
                const anticipationDate = new Date(item.anticipationDate);
                const now = new Date();

                if (anticipationDate < now) {
                    colorCode = 'yellow'; // Anticipation date passed
                } else if (item.scheduledDate) {
                    colorCode = 'green'; // Scheduled
                }
            }

            return {
                ...item,
                colorCode
            };
        });
    }

    // Computed: Calendar view data
    get calendarViewData() {
        const data = new Map<string, Map<string, any[]>>();

        // Group schedules by date and form
        this.filteredSchedules.forEach((schedule: any) => {
            if (!schedule.scheduledDate) return;

            const dateKey = new Date(schedule.scheduledDate).toDateString();
            const formId = schedule.formId || 'unassigned';

            if (!data.has(dateKey)) {
                data.set(dateKey, new Map());
            }

            const dateData = data.get(dateKey)!;
            if (!dateData.has(formId)) {
                dateData.set(formId, []);
            }

            dateData.get(formId)!.push(schedule);
        });

        return data;
    }

    // Computed: List view data
    get listViewData() {
        const sorted = [...this.itemsWithColorCoding];

        // Apply sorting
        sorted.sort((a: any, b: any) => {
            // Sort by color priority first
            const colorPriority: Record<string, number> = {
                'red': 0,
                'yellow': 1,
                'white': 2,
                'green': 3
            };

            const aPriority = colorPriority[a.colorCode] || 999;
            const bPriority = colorPriority[b.colorCode] || 999;

            if (aPriority !== bPriority) {
                return aPriority - bPriority;
            }

            // Then by priority
            if (a.priority !== b.priority) {
                return (b.priority || 0) - (a.priority || 0);
            }

            // Then by anticipation date
            if (a.anticipationDate && b.anticipationDate) {
                return new Date(a.anticipationDate).getTime() - new Date(b.anticipationDate).getTime();
            }

            return 0;
        });

        return sorted;
    }

    // Computed: Capacity by date and form
    get capacityByDateAndForm() {
        const capacity = new Map<string, Map<string, any>>();

        // Get capacity data from service store
        const capacityStore = this.rootStore.productionCapacityStore as any;
        const capacities = capacityStore?.entities || [];

        capacities.forEach((cap: any) => {
            const dateKey = new Date(cap.date).toDateString();

            if (!capacity.has(dateKey)) {
                capacity.set(dateKey, new Map());
            }

            capacity.get(dateKey)!.set(cap.formId, {
                total: cap.totalCapacity,
                used: cap.usedCapacity,
                available: cap.totalCapacity - cap.usedCapacity,
                percentage: (cap.usedCapacity / cap.totalCapacity) * 100
            });
        });

        // Add scheduled items to capacity
        this.schedules.forEach((schedule: any) => {
            if (!schedule.scheduledDate || !schedule.formId) return;

            const dateKey = new Date(schedule.scheduledDate).toDateString();

            if (!capacity.has(dateKey)) {
                capacity.set(dateKey, new Map());
            }

            const dateCapacity = capacity.get(dateKey)!;
            if (!dateCapacity.has(schedule.formId)) {
                dateCapacity.set(schedule.formId, {
                    total: 10, // Default capacity
                    used: 0,
                    available: 10,
                    percentage: 0
                });
            }

            const formCapacity = dateCapacity.get(schedule.formId)!;
            formCapacity.used += 1;
            formCapacity.available = formCapacity.total - formCapacity.used;
            formCapacity.percentage = (formCapacity.used / formCapacity.total) * 100;
        });

        return capacity;
    }

    // Computed: Scheduling conflicts
    get conflicts() {
        const conflicts: any[] = [];

        this.capacityByDateAndForm.forEach((dateCapacity, dateKey) => {
            dateCapacity.forEach((capacity, formId) => {
                if (capacity.percentage > 100) {
                    conflicts.push({
                        date: new Date(dateKey),
                        formId,
                        overCapacityBy: capacity.used - capacity.total,
                        items: this.schedules.filter((s: any) =>
                            s.scheduledDate &&
                            new Date(s.scheduledDate).toDateString() === dateKey &&
                            s.formId === formId
                        )
                    });
                }
            });
        });

        return conflicts;
    }

    // Filtered schedules based on filters
    get filteredSchedules() {
        let filtered = [...this.schedules, ...this.salesOrderLines];

        // Apply department filter
        if (this.filters.department) {
            filtered = filtered.filter((item: any) =>
                item.departmentId === this.filters.department
            );
        }

        // Apply sub-department filter
        if (this.filters.subDepartment) {
            filtered = filtered.filter((item: any) =>
                item.subDepartmentId === this.filters.subDepartment
            );
        }

        // Apply form filter
        if (this.filters.form) {
            filtered = filtered.filter((item: any) =>
                item.formId === this.filters.form
            );
        }

        // Apply priority range filter
        if (this.filters.priorityRange.min !== null) {
            filtered = filtered.filter((item: any) =>
                (item.priority || 0) >= this.filters.priorityRange.min!
            );
        }
        if (this.filters.priorityRange.max !== null) {
            filtered = filtered.filter((item: any) =>
                (item.priority || 0) <= this.filters.priorityRange.max!
            );
        }

        // Apply date range filter
        if (this.filters.dateRange.start) {
            filtered = filtered.filter((item: any) => {
                const itemDate = item.anticipationDate || item.scheduledDate;
                if (!itemDate) return this.filters.hasAnticipationDate === false;
                return new Date(itemDate) >= this.filters.dateRange.start;
            });
        }
        if (this.filters.dateRange.end) {
            filtered = filtered.filter((item: any) => {
                const itemDate = item.anticipationDate || item.scheduledDate;
                if (!itemDate) return false;
                return new Date(itemDate) <= this.filters.dateRange.end;
            });
        }

        // Apply anticipation date filter
        if (this.filters.hasAnticipationDate !== null) {
            filtered = filtered.filter((item: any) => {
                const hasDate = !!item.anticipationDate;
                return hasDate === this.filters.hasAnticipationDate;
            });
        }

        // Apply status filter
        if (this.filters.status !== 'all') {
            switch (this.filters.status) {
                case 'scheduled':
                    filtered = filtered.filter((item: any) => item.scheduledDate);
                    break;
                case 'released':
                    filtered = filtered.filter((item: any) =>
                        item.status === 'released' && !item.scheduledDate
                    );
                    break;
                case 'completed':
                    filtered = filtered.filter((item: any) => item.status === 'completed');
                    break;
                case 'issues':
                    filtered = filtered.filter((item: any) => {
                        if (!item.anticipationDate) return true;
                        const anticipationDate = new Date(item.anticipationDate);
                        return anticipationDate < new Date();
                    });
                    break;
            }
        }

        return filtered;
    }

    // Get available forms for a specific date
    getAvailableFormsForDate(date: Date) {
        const dateKey = date.toDateString();
        const dateCapacity = this.capacityByDateAndForm.get(dateKey) || new Map();

        return this.forms.filter((form: any) => {
            const formCapacity = dateCapacity.get(form.id);
            if (!formCapacity) return true; // No capacity data, assume available
            return formCapacity.available > 0;
        });
    }

    // Check capacity for a specific date and form
    checkCapacity(date: Date, formId: string, quantity: number = 1) {
        const dateKey = date.toDateString();
        const dateCapacity = this.capacityByDateAndForm.get(dateKey);

        if (!dateCapacity) {
            return { available: true, capacity: null, message: 'Capacity available' };
        }

        const formCapacity = dateCapacity.get(formId);
        if (!formCapacity) {
            return { available: true, capacity: null, message: 'Capacity available' };
        }

        if (formCapacity.available >= quantity) {
            return {
                available: true,
                capacity: formCapacity,
                message: `Capacity available (${formCapacity.available} slots)`
            };
        }

        // Find earliest available date
        const earliestDate = new Date(date);
        for (let i = 1; i <= 30; i++) {
            earliestDate.setDate(earliestDate.getDate() + 1);
            const futureCapacity = this.checkCapacity(earliestDate, formId, quantity);
            if (futureCapacity.available) {
                return {
                    available: false,
                    capacity: formCapacity,
                    message: `No capacity. Earliest date: ${earliestDate.toLocaleDateString()}`,
                    earliestDate
                };
            }
        }

        return {
            available: false,
            capacity: formCapacity,
            message: 'No capacity available in next 30 days',
            earliestDate: null
        };
    }

    // ========================================
    // UI Actions - Only manage UI state
    // ========================================

    selectSchedule(scheduleId: string | null) {
        this.selectedScheduleId = scheduleId;
    }

    selectDepartment(departmentId: string | null) {
        this.selectedDepartmentId = departmentId;
        this.filters.department = departmentId;
    }

    selectForm(formId: string | null) {
        this.selectedFormId = formId;
        this.filters.form = formId;
    }

    setSelectedDate(date: Date) {
        this.selectedDate = date;
    }

    setViewMode(mode: typeof this.viewMode) {
        this.viewMode = mode;
    }

    setCalendarView(view: typeof this.calendarView) {
        this.calendarView = view;
    }

    toggleCapacityIndicators() {
        this.showCapacityIndicators = !this.showCapacityIndicators;
    }

    toggleConflicts() {
        this.showConflicts = !this.showConflicts;
    }

    setDateRange(start: Date, end: Date) {
        this.filters.dateRange.start = start;
        this.filters.dateRange.end = end;
    }

    setPriorityRange(min: number | null, max: number | null) {
        this.filters.priorityRange.min = min;
        this.filters.priorityRange.max = max;
    }

    setFilter(filterKey: keyof typeof this.filters, value: any) {
        (this.filters as any)[filterKey] = value;
    }

    clearFilters() {
        this.filters = {
            department: null,
            subDepartment: null,
            form: null,
            priorityRange: {
                min: null,
                max: null
            },
            dateRange: {
                start: new Date(),
                end: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)
            },
            hasAnticipationDate: null,
            status: 'all'
        };
    }

    // Bulk selection
    toggleSelectMode() {
        this.selectMode = !this.selectMode;
        if (!this.selectMode) {
            this.selectedItems.clear();
        }
    }

    toggleItemSelection(itemId: string) {
        if (this.selectedItems.has(itemId)) {
            this.selectedItems.delete(itemId);
        } else {
            this.selectedItems.add(itemId);
        }
    }

    selectAll() {
        this.filteredSchedules.forEach((item: any) => {
            this.selectedItems.add(item.id);
        });
    }

    clearSelection() {
        this.selectedItems.clear();
    }

    // Dialog Management
    openDialog(dialogName: keyof typeof this.dialogs) {
        this.dialogs[dialogName] = true;
    }

    closeDialog(dialogName: keyof typeof this.dialogs) {
        this.dialogs[dialogName] = false;
    }

    // Form Assignment
    startFormAssignment(itemId: string) {
        this.formAssignment.itemId = itemId;

        // Get suggested forms based on product
        const item = this.schedules.find((s: any) => s.id === itemId);
        if (item && item.productId) {
            this.suggestFormsForProduct(item.productId);
        }

        this.openDialog('formAssignment');
    }

    suggestFormsForProduct(productId: string) {
        // This would use AI or mapping table to suggest forms
        // For now, just return forms from the same department
        const product = (this.rootStore.productsStore as any)?.entities?.find(
            (p: any) => p.id === productId
        );

        if (product && product.departmentId) {
            this.formAssignment.suggestedForms = this.forms.filter((form: any) =>
                form.departmentId === product.departmentId
            ).slice(0, 3); // Top 3 suggestions
        }
    }

    assignForm(formId: string, rememberMapping: boolean = false) {
        this.formAssignment.selectedFormId = formId;
        this.formAssignment.rememberMapping = rememberMapping;

        if (this.formAssignment.itemId) {
            this.updateScheduleItem(this.formAssignment.itemId, { formId });

            if (rememberMapping) {
                // Save mapping for future use
                this.saveProductFormMapping(this.formAssignment.itemId, formId);
            }
        }

        this.closeDialog('formAssignment');
        this.resetFormAssignment();
    }

    resetFormAssignment() {
        this.formAssignment = {
            itemId: null,
            suggestedForms: [],
            selectedFormId: null,
            rememberMapping: false
        };
    }

    // Capacity Request
    startCapacityRequest(itemId: string, requestedDate: Date) {
        this.capacityRequest.itemId = itemId;
        this.capacityRequest.requestedDate = requestedDate;

        // Check capacity and get earliest available
        const capacity = this.checkCapacity(requestedDate, this.selectedFormId || '', 1);
        this.capacityRequest.availableDate = capacity.earliestDate || null;

        this.openDialog('requestSchedule');
    }

    submitCapacityRequest() {
        // Submit request for approval
        if (this.capacityRequest.itemId && this.capacityRequest.requestedDate) {
            this.createScheduleRequest({
                itemId: this.capacityRequest.itemId,
                requestedDate: this.capacityRequest.requestedDate,
                availableDate: this.capacityRequest.availableDate,
                justification: this.capacityRequest.justification,
                priority: this.capacityRequest.priority
            });
        }

        this.closeDialog('requestSchedule');
        this.resetCapacityRequest();
    }

    resetCapacityRequest() {
        this.capacityRequest = {
            itemId: null,
            requestedDate: null,
            availableDate: null,
            justification: '',
            priority: 5
        };
    }

    // Drag and Drop
    startDrag(itemId: string) {
        this.draggedItemId = itemId;
    }

    dragOver(date: Date, formId: string) {
        this.dragOverDate = date;
        this.dragOverFormId = formId;
    }

    drop() {
        if (this.draggedItemId && this.dragOverDate && this.dragOverFormId) {
            // Check capacity before dropping
            const capacity = this.checkCapacity(this.dragOverDate, this.dragOverFormId);

            if (capacity.available) {
                this.scheduleItem(this.draggedItemId, this.dragOverDate, this.dragOverFormId);
            } else {
                // Show capacity warning
                this.startCapacityRequest(this.draggedItemId, this.dragOverDate);
            }
        }

        this.cancelDrag();
    }

    cancelDrag() {
        this.draggedItemId = null;
        this.dragOverDate = null;
        this.dragOverFormId = null;
    }

    // ========================================
    // Service Store Actions - Delegate to ServiceStore
    // ========================================

    async loadSchedules() {
        const schedulesStore = this.rootStore.productionSchedulesStore as any;
        if (schedulesStore?.find) {
            await schedulesStore.find({
                query: {
                    $limit: 1000,
                    $sort: {
                        anticipationDate: 1
                    }
                }
            });
        }
    }

    async loadDepartments() {
        const deptStore = this.rootStore.departmentsStore as any;
        if (deptStore?.find) {
            await deptStore.find({
                query: {
                    $limit: 100
                }
            });
        }
    }

    async loadForms() {
        const formsStore = this.rootStore.productionFormsStore as any;
        if (formsStore?.find) {
            await formsStore.find({
                query: {
                    $limit: 500
                }
            });
        }
    }

    async loadCapacity(startDate: Date, endDate: Date) {
        const capacityStore = this.rootStore.productionCapacityStore as any;
        if (capacityStore?.find) {
            await capacityStore.find({
                query: {
                    date: {
                        $gte: startDate.toISOString(),
                        $lte: endDate.toISOString()
                    },
                    $limit: 1000
                }
            });
        }
    }

    async scheduleItem(itemId: string, date: Date, formId: string) {
        const schedulesStore = this.rootStore.productionSchedulesStore as any;
        if (schedulesStore?.patch) {
            return await schedulesStore.patch(itemId, {
                scheduledDate: date.toISOString(),
                formId,
                status: 'scheduled'
            });
        }
    }

    async rescheduleItem(itemId: string, newDate: Date) {
        const schedulesStore = this.rootStore.productionSchedulesStore as any;
        if (schedulesStore?.patch) {
            return await schedulesStore.patch(itemId, {
                scheduledDate: newDate.toISOString()
            });
        }
    }

    async bulkReschedule(itemIds: string[], newDate: Date) {
        const schedulesStore = this.rootStore.productionSchedulesStore as any;
        if (schedulesStore?.patch) {
            const promises = itemIds.map(id =>
                schedulesStore.patch(id, {
                    scheduledDate: newDate.toISOString()
                })
            );
            return await Promise.all(promises);
        }
    }

    async updateScheduleItem(itemId: string, updates: any) {
        const schedulesStore = this.rootStore.productionSchedulesStore as any;
        if (schedulesStore?.patch) {
            return await schedulesStore.patch(itemId, updates);
        }
    }

    async createScheduleRequest(request: any) {
        const requestsStore = this.rootStore.scheduleRequestsStore as any;
        if (requestsStore?.create) {
            return await requestsStore.create(request);
        }
    }

    async saveProductFormMapping(productId: string, formId: string) {
        const mappingsStore = this.rootStore.productFormMappingsStore as any;
        if (mappingsStore?.create) {
            return await mappingsStore.create({
                productId,
                formId,
                confidence: 100
            });
        }
    }

    // Initialize
    async initialize() {
        await Promise.all([
            this.loadSchedules(),
            this.loadDepartments(),
            this.loadForms(),
            this.loadCapacity(this.filters.dateRange.start, this.filters.dateRange.end)
        ]);
    }

    // Cleanup
    dispose() {
        this.selectedScheduleId = null;
        this.selectedDepartmentId = null;
        this.selectedFormId = null;
        this.clearFilters();
        this.clearSelection();
        this.cancelDrag();
        this.resetFormAssignment();
        this.resetCapacityRequest();

        // Close all dialogs
        Object.keys(this.dialogs).forEach(key => {
            (this.dialogs as any)[key] = false;
        });
    }
}

export default ProductionScheduleViewStore;